import { create } from 'zustand'
import type { ConfigResponse, RecordRow, Tag, Value, ViewResponse } from '@/shared/api/generated'
import { api, getErrorMessage } from '@/shared/api/generated'
import { toast } from '@/shared/stores/toastStore'

export interface RegistryState {
  config: ConfigResponse | null
  records: RecordRow[]
  tags: Tag[]
  values: Record<number, Value[]>
  views: ViewResponse[]  // group views for personal view toggle  // tag_id → values

  tagFilters: Record<number, string>
  columnFilters: Record<string, string>
  globalFilter: string

  expanded: boolean
  sortColumn: string
  sortDirection: 'asc' | 'desc'

  // Pagination
  hasMore: boolean

  editingRecord: RecordRow | null  // null = create mode when editorOpen
  editorGroupId: number
  editorOpen: boolean

  loadConfig: () => Promise<void>
  loadRecords: (groupId: number) => Promise<void>
  loadAllValues: (groupId?: number) => Promise<void>
  loadViews: (groupId: number) => Promise<void>
  setTagFilter: (tagId: number, text: string) => void
  setColumnFilter: (column: string, text: string) => void
  setGlobalFilter: (text: string) => void
  toggleExpanded: () => void
  setSort: (column: string) => void
  openEditor: (record: RecordRow | null, groupId: number) => void
  closeEditor: () => void
  reset: () => void
}

const STORAGE_KEY = 'registry-expanded'

function getPersistedExpanded(groupId: number): boolean {
  try {
    const data = localStorage.getItem(STORAGE_KEY)
    if (data) {
      const map = JSON.parse(data)
      return map[groupId] === true
    }
  } catch (err) { toast.error('Failed to read registry preference: ' + getErrorMessage(err)) }
  return false
}

function persistExpanded(groupId: number, expanded: boolean) {
  try {
    const data = localStorage.getItem(STORAGE_KEY)
    const map = data ? JSON.parse(data) : {}
    map[groupId] = expanded
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map))
  } catch (err) { toast.error('Failed to save registry preference: ' + getErrorMessage(err)) }
}

export const useRegistryStore = create<RegistryState>()((set, get) => ({
  config: null,
  records: [],
  tags: [],
  values: {},
  views: [],
  tagFilters: {},
  columnFilters: {},
  globalFilter: '',
  expanded: false,
  sortColumn: 'id',
  sortDirection: 'desc',
  hasMore: false,
  editingRecord: null,
  editorGroupId: 0,
  editorOpen: false,

  loadConfig: async () => {
    try {
      const config = await api.getRegistryConfig()
      set({ config, tags: config.tags || [] })
    } catch (err) { toast.error(getErrorMessage(err)) }
  },

  loadRecords: async (groupId: number) => {
    set({ expanded: getPersistedExpanded(groupId) })
    try {
      const page = await api.listRecords(groupId)
      set({ records: page.records || [], hasMore: page.has_more })

      let cursor = page.cursor
      while (cursor) {
        const next = await api.listRecords(groupId, cursor, 1000)
        set(s => ({
          records: [...s.records, ...(next.records || [])],
          hasMore: next.has_more,
        }))
        cursor = next.cursor
      }
    } catch (err) {
      toast.error(getErrorMessage(err))
      set({ records: [], hasMore: false })
    }
  },

  loadAllValues: async (groupId?: number) => {
    const { tags } = get()
    const vals: Record<number, Value[]> = {}
    await Promise.all(tags.map(async (tag) => {
      try { vals[tag.id] = await api.listValues(tag.id, groupId) } catch (err) { toast.error(getErrorMessage(err)); vals[tag.id] = [] }
    }))
    set({ values: vals })
  },

  loadViews: async (groupId: number) => {
    try {
      const views = await api.listGroupViews(groupId)
      set({ views })
    } catch (err) { toast.error(getErrorMessage(err)); set({ views: [] }) }
  },

  setTagFilter: (tagId, text) => set(s => ({
    tagFilters: { ...s.tagFilters, [tagId]: text },
  })),

  setColumnFilter: (column, text) => set(s => ({
    columnFilters: { ...s.columnFilters, [column]: text },
  })),

  setGlobalFilter: (text) => set({ globalFilter: text }),

  toggleExpanded: () => set(s => {
    const next = !s.expanded
    return { expanded: next }
  }),

  setSort: (column) => set(s => ({
    sortColumn: column,
    sortDirection: s.sortColumn === column && s.sortDirection === 'asc' ? 'desc' : 'asc',
  })),

  openEditor: (record, groupId) => set({ editingRecord: record, editorGroupId: groupId, editorOpen: true }),
  closeEditor: () => set({ editingRecord: null, editorOpen: false }),

  reset: () => set({
    records: [],
    tagFilters: {},
    columnFilters: {},
    globalFilter: '',
    expanded: false,
    editorOpen: false,
    editingRecord: null,
    hasMore: false,
  }),
}))

// Persist expanded state when it changes (called from component)
export function persistRegistryExpanded(groupId: number, expanded: boolean) {
  persistExpanded(groupId, expanded)
}
